import axios from 'axios';
import { TenantConfig } from '@repo/types';
import API from '../endpoints';
import { useQuery } from '@tanstack/react-query';
import { getApiUrlOverride } from './getApiUrlOverride';

// Cache tenant config for the entire session (practically immutable)
const CACHE_TIME = 24 * 60 * 60 * 1000; // 24 hours

export const TENANT_CONFIG_QUERY_KEY = ['tenant-config'] as const;

// Type-safe access to Node.js process global (may not exist in browser)
const getNodeEnv = (): string | undefined => {
  const g = globalThis as any;
  return g.process?.env?.NODE_ENV;
};

/**
 * Bootstrap API URL used for the initial tenant config fetch.
 * In production, this points to the main API server.
 * In development/local, it defaults to localhost.
 *
 * Priority:
 * 1. VITE_API_URL environment variable (if set in .env file)
 * 2. NODE_ENV-based default (production/test/development)
 *
 * Note: This is the only query that needs a hardcoded URL because:
 * 1. It's pre-fetched during SSR with the server-side bootstrap URL
 * 2. The client typically has the data hydrated from SSR
 * 3. For rare client-side refetches, we use this fallback
 */
const getBootstrapApiUrl = (): string => {
  // Check for VITE_API_URL override first
  const override = getApiUrlOverride();
  if (override) {
    return override;
  }

  // Check if we're on the server
  if (typeof window === 'undefined') {
    // Respect NODE_ENV on server-side (SSR)
    const nodeEnv = getNodeEnv();
    if (nodeEnv === 'production') {
      return 'https://api.streamlinecontent.tech';
    }
    if (nodeEnv === 'test') {
      return 'https://api-test.streamlinecontent.tech';
    }
    // Default to localhost for development
    return 'http://localhost:3002';
  }

  // On the client, check if we have a previously loaded API URL in query cache
  // This handles the case where the config was hydrated from SSR
  // If not available, fall back to production URL for safety
  return 'https://api.streamlinecontent.tech';
};

const fetchTenantConfig = async (): Promise<TenantConfig> => {
  const bootstrapUrl = getBootstrapApiUrl();
  const { data } = await axios.get<TenantConfig>(`${bootstrapUrl}${API.endpoints.tenantConfig}`);

  // Apply VITE_API_URL override to the tenant config's apiUrl if set
  // This ensures all queries using tenantConfig.env.apiUrl get the override
  const override = getApiUrlOverride();
  if (override && data.env) {
    return {
      ...data,
      env: {
        ...data.env,
        apiUrl: override,
      },
    };
  }

  return data;
};

/**
 * Hook to fetch tenant configuration from the API.
 * The tenant ID is determined by the x-tenant-id header which axios
 * automatically includes via the interceptor.
 *
 * This query is designed to be:
 * 1. Pre-fetched on the server during SSR
 * 2. Hydrated to the client via React Query dehydration
 * 3. Never refetched on the client (uses cached data)
 */
export const useTenantConfigQuery = () => {
  return useQuery({
    queryKey: TENANT_CONFIG_QUERY_KEY,
    queryFn: fetchTenantConfig,
    staleTime: CACHE_TIME,
    gcTime: CACHE_TIME,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

/**
 * Standalone function to fetch tenant config.
 * Used by the server during SSR when a QueryClient isn't available yet.
 *
 * @param apiUrl - The API base URL to use for the request
 * @param tenantId - The tenant ID to fetch config for
 */
export const fetchTenantConfigServer = async (apiUrl: string, tenantId: string): Promise<TenantConfig> => {
  const response = await fetch(`${apiUrl}${API.endpoints.tenantConfig}`, {
    headers: {
      'x-tenant-id': tenantId,
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch tenant config: ${response.status} ${response.statusText}`);
  }

  return response.json();
};
